<script setup>
    const dayjs = useDayjs();
    import { parseTime } from '~~/shared/utilities/time';
    import { useBrowserListener } from '@/composables/browser/listeners';
    import { scrollIntoView } from '@/composables/helpers/client-only/scroll';
    import SessionsCalendarCard from '~/components/blocks/two-six/sessions/calendar-card.vue';

    const { $clientPosthog } = useNuxtApp();

    const props = defineProps({
        data: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        day: {
            type: String,
            default: '',
        },
        showFullCard: {
            type: Boolean,
            default: false,
        },
        start_time: {
            type: String,
            default: '09:00',
        },
        end_time: {
            type: String,
            default: '18:00',
        },
        groupby: {
            type: String,
            default: 'location',
        },
        suggest_slots: {
            type: Boolean,
            default: false,
        },
        results_count: {
            type: String,
            default: '',
        },
    });

    const week_schedule = computed(() => {
        if (!props.data?.sessions || props.groupby === 'location') return [];
        const grouped = {};

        props.data.sessions.forEach((session) => {
            // Assume session.start_date_and_time is ISO string
            const date = dayjs(session.start_date_and_time).tz('America/Los_Angeles');
            // Format as YYYY-MM-DD
            const dayKey = date.format('ddd, MMM D, YYYY');
            if (!grouped[dayKey]) grouped[dayKey] = { name: dayKey, day: date.toISOString(), sessions: [], index: 0 };
            grouped[dayKey].sessions.push(session);
        });

        // Sort days
        const ordered = Object.values(grouped).sort((a, b) => new Date(a.day) - new Date(b.day));

        // Sort sessions within each day
        ordered.forEach((group) => {
            group.sessions.sort((a, b) => new Date(a.start_date_and_time) - new Date(b.start_date_and_time));
        });

        // Assign index for grid positioning
        ordered.forEach((group, idx) => {
            group.index = idx + 1;
        });

        return ordered;
    });

    const location_schedule = computed(() => {
        if (!props.data?.sessions || props.groupby !== 'location') return [];
        const grouped = {};

        props.data.sessions.forEach((session) => {
            if (Array.isArray(session.locations)) {
                session.locations.forEach((location) => {
                    if (!grouped[location.id]) grouped[location.id] = { name: location.name, location: location, sessions: [], index: 0 };
                    grouped[location.id]['sessions'].push(session);
                });
            }
        });

        grouped._ordered = Object.values(grouped).sort((a, b) => (a.location.order ?? 0) - (b.location.order ?? 0));

        Object.keys(grouped._ordered).forEach((location) => {
            grouped._ordered[location]['sessions'].sort((a, b) => new Date(a.start_date_and_time) - new Date(b.start_date_and_time));
        });

        let counter = 0;
        Object.keys(grouped._ordered).forEach((location) => {
            grouped._ordered[location]['index'] = ++counter;
        });

        return grouped._ordered;
    });

    const schedule = computed(() => {
        if (props.groupby === 'location') {
            return location_schedule.value;
        } else {
            return week_schedule.value;
        }
    });

    const time_blocks = computed(() => {
        if (!props.start_time || !props.end_time) return [];

        const today = dayjs(props.day).format('YYYY-MM-DD');
        const [startHour, startMinute] = parseTime(props.start_time);
        const [endHour, endMinute] = parseTime(props.end_time);
        // Use dayjs to construct valid date objects and set hour/minute manually
        let start = dayjs(today).hour(startHour).minute(startMinute).second(0).millisecond(0);
        let end = dayjs(today).hour(endHour).minute(endMinute).second(0).millisecond(0);

        start = start.minute(0);
        if (end.minute() !== 0) end = end.add(1, 'hour').minute(0);

        const blocks = [];

        for (let time = start; time.isBefore(end); time = time.add(15, 'minutes')) {
            blocks.push({
                hour: time.hour(),
                minute: time.minute(),
                label: time.minute() === 0 ? time.format('h:mma') + ' PT' : '',
                mobileLabel: time.minute() === 0 ? time.format('ha') + ' PT' : '',
                start: time.toISOString(),
                end: time.add(15, 'minutes').toISOString(),
            });
        }

        return blocks;
    });

    const empty_hour_blocks = computed(() => {
        if (!props.suggest_slots || !props.data?.sessions || props.groupby !== 'location') return [];
        if (!time_blocks.value.length || !location_schedule.value.length) return [];

        // Build a map of sessions by location and by time block
        const sessionsByLocation = {};
        location_schedule.value.forEach((group) => {
            sessionsByLocation[group.location.id] = group.sessions;
        });

        const emptyBlocks = [];

        // For each hour block, check if all locations have no session in that hour
        for (let i = 0; i < time_blocks.value.length; i += 4) {
            // 4 blocks per hour (15 min each)
            const block = time_blocks.value[i];
            const hourStart = dayjs.tz(`${props.day} ${block.hour}:${block.minute}`, 'YYYY-MM-DD H:m', 'America/Los_Angeles').utc();
            const hourEnd = hourStart.add(1, 'hour').tz('America/Los_Angeles');

            let allEmpty = true;

            for (const group of location_schedule.value) {
                const hasSession = group.sessions.some((session) => {
                    const sessionStart = dayjs(session.start_date_and_time).tz('America/Los_Angeles');
                    const sessionEnd = dayjs(session.end_date_and_time).tz('America/Los_Angeles');
                    // Check if session overlaps with this hour
                    const isInRange = sessionStart.isBefore(hourEnd) && sessionEnd.isAfter(hourStart);
                    return isInRange;
                });
                if (hasSession) {
                    allEmpty = false;
                    break;
                }
            }

            if (allEmpty) {
                emptyBlocks.push({
                    hour: block.hour,
                    minute: block.minute,
                    label: block.label,
                    index: i,
                    hourStart: hourStart.toISOString(),
                    hourEnd: hourEnd.toISOString(),
                    // Grid positioning
                    grid_row_start: i + 2, // +2 because grid row 1 is header, and grid is 1-based
                    grid_row_span: 4, // 4 blocks per hour (15 min each)
                    grid_col_start: 1, // spans all columns
                    grid_col_span: location_schedule.value.length,
                });
            }
        }

        // Condense consecutive empty blocks into larger blocks
        for (let i = 0; i < emptyBlocks.length - 1; ) {
            let j = i;
            while (j < emptyBlocks.length - 1 && emptyBlocks[j].hourEnd === emptyBlocks[j + 1].hourStart) {
                j++;
            }
            if (j > i) {
                // Merge blocks from i to j
                emptyBlocks[i].hourEnd = emptyBlocks[j].hourEnd;
                emptyBlocks[i].grid_row_span = (j - i + 1) * 4;
                // Remove merged blocks
                emptyBlocks.splice(i + 1, j - i);
            } else {
                i++;
            }
        }

        return emptyBlocks;
    });

    const schedule_grid = ref(null);
    const gridCanGoLeft = ref(false);
    const gridCanGoRight = ref(false);
    const timeline = ref(null);
    const maxGridWidth = ref(`100dvw`); // Default to 100dvw, will be updated on mount
    const headers = ref(null)
    const sticky = ref(null)
    const sticky_active = ref(false)


    const gridWidthExcludingFirstColumn = () => {
        // Only applies when day !== 'all' (grid mode)
        if (props.day === 'all') return 0;
        if (!schedule_grid.value) return '100dvw';
        // Get the actual visible width of the grid (clientWidth)
        const gridWidth = schedule_grid.value.clientWidth;
        const remInPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const widthExcludingTimeline = gridWidth - 32;
        // Convert back to rem for CSS
        maxGridWidth.value = `${widthExcludingTimeline / remInPx}rem`;
    };



    const scrollToLeft = () => {
        if (schedule_grid.value) {
            $clientPosthog?.capture('calendar_scroll_left', {
                day: props.day,
                groupby: props.groupby,
                current_scroll_position: schedule_grid.value.scrollLeft
            });
            schedule_grid.value.scrollTo({
                left: schedule_grid.value.scrollLeft - schedule_grid.value.clientWidth,
                behavior: 'smooth',
            });
        }
    };

    const scrollToRight = () => {
        if (schedule_grid.value) {
            $clientPosthog?.capture('calendar_scroll_right', {
                day: props.day,
                groupby: props.groupby,
                current_scroll_position: schedule_grid.value.scrollLeft
            });
            schedule_grid.value.scrollTo({
                left: schedule_grid.value.scrollLeft + schedule_grid.value.clientWidth,
                behavior: 'smooth',
            });
        }
    };

    const handleScroll = () => {
        if (schedule_grid.value) {
            gridCanGoLeft.value = schedule_grid.value.scrollLeft > 0;
            gridCanGoRight.value = schedule_grid.value.scrollLeft < schedule_grid.value.scrollWidth - schedule_grid.value.clientWidth;
        }
        if (headers.value && schedule_grid.value) {
            headers.value.scrollLeft = schedule_grid.value.scrollLeft;
        }
    };

    const handleSuggestClick = async (hourStart, hourEnd) => {
        // Convert ISO strings to dayjs objects in Los Angeles timezone
        const startTime = dayjs(hourStart).tz('America/Los_Angeles');
        const endTime = dayjs(hourEnd).tz('America/Los_Angeles');

        // Calculate minutes from 9am (start_time) for both times
        const dayStartMinutes = 9 * 60; // 9am in minutes
        const dayTotalMinutes = 9 * 60; // 9 hours (9am-6pm) in minutes

        const startMinutes = startTime.hour() * 60 + startTime.minute() - dayStartMinutes;
        const endMinutes = endTime.hour() * 60 + endTime.minute() - dayStartMinutes;

        // Convert to percentage of the day (0-100)
        const startPercent = Math.max(0, Math.round((startMinutes / dayTotalMinutes) * 100));
        const endPercent = Math.min(100, Math.round((endMinutes / dayTotalMinutes) * 100));

        $clientPosthog?.capture('calendar_suggest_slot_clicked', {
            day: props.day,
            hour_start: startTime.format('h:mma'),
            hour_end: endTime.format('h:mma'),
            time_range_start_percent: startPercent,
            time_range_end_percent: endPercent
        });

        // Create query parameters for navigation
        const queryString = {
            tab: 'sessions',
            day: props.day,
            view: 'calendar',
            time_range: [startPercent, endPercent],
        };
        // Scroll to the timeline element so the refresh is at the top
        scrollIntoView(timeline.value, { behavior: 'smooth' });
        // Navigate to sessions page with query parameters
        await navigateTo({
            path: '/2025-sessions',
            query: queryString,
        });
    };

    watch(() => props.data, () => {
        if (props.suggest_slots && process.client) gridWidthExcludingFirstColumn();
    }, { immediate: true });

    const onWindowScroll = () => {
        if (!schedule_grid.value || !headers.value) return;
        const gridTop = schedule_grid.value.getBoundingClientRect().top + window.scrollY;
        const scrolled = window.scrollY > gridTop + 50;
        sticky_active.value = scrolled;
    };

    onMounted(() => {
        if (schedule_grid.value && process.client) {
            window.addEventListener('scroll', onWindowScroll, { passive: true });
            schedule_grid.value.addEventListener('scroll', handleScroll, { passive: true });
            handleScroll();
        }
    });

    useBrowserListener('resize', () => {
        if (props.suggest_slots && schedule_grid.value && process.client) {
            gridWidthExcludingFirstColumn();
            handleScroll();
        }
    });

    onBeforeUnmount(() => {
        if (schedule_grid.value && process.client) schedule_grid.value.removeEventListener('scroll', handleScroll);
        window.removeEventListener('scroll', onWindowScroll);
    });

</script>
<template>
    <div class="sessions-calendar" :class="{ loading: loading, day: day !== 'all' }" :style="`--columns: ${Math.max(schedule.length, 4)}; --rows: ${time_blocks.length}`">
        <div class="calendar-controls flex items-center justify-end mb-5.25 lg:mb-10.25">
            <p class="result-count" v-if="results_count" v-html="results_count"></p>
            <UButton icon="local-arrow-left" variant="left-tab" color="transparent" :disabled="!gridCanGoLeft" @click.prevent="scrollToLeft"></UButton>
            <UButton icon="local-arrow-right" variant="right-tab" color="transparent" :disabled="!gridCanGoRight" @click.prevent="scrollToRight"></UButton>
        </div>
        <div class="schedule" :class="{ loading: loading, day: day !== 'all', all: day === 'all' }">
            <div class="sticky-list" ref="sticky" :class="{ active: sticky_active }" :inert="!sticky_active">
                <div class="timeline" :style="`--index: 0`" inert tabindex="-1"></div>
                <ul class="tracks" ref="headers">
                    <li v-for="(group, index) in schedule" :key="group.index" class="track" :class="{ alt: index % 2 !== 0 }" :style="`--index: ${group.index}`">
                        <h3 class="track-name" :style="`--index: ${group.index}`">
                            {{ group.name }}
                            <span class="desc" v-if="group.location?.description" v-html="group.location.description"></span>
                        </h3>
                    </li>
                </ul>
                <div class="controls">
                    <UButton icon="local-arrow-left" variant="left-tab" color="transparent" :disabled="!gridCanGoLeft" @click.prevent="scrollToLeft"></UButton>
                    <UButton icon="local-arrow-right" variant="right-tab" color="transparent" :disabled="!gridCanGoRight" @click.prevent="scrollToRight"></UButton>
                </div>
            </div>
            <div v-if="day !== 'all'" class="timeline" :style="`--index: 1`" inert tabindex="-1" ref="timeline">
                <template v-for="(block, index) in time_blocks" :key="block.mobileLabel" class="time-block">
                    <span class="time-label desktop" v-if="block.label" :style="`--starts: ${index + 2}`" inert tabindex="-1">{{ block.label }}</span>
                    <span class="time-label mobile" v-if="block.mobileLabel" :style="`--starts: ${index + 2}`" inert tabindex="-1">{{ block.mobileLabel }}</span>
                </template>
            </div>
            <div class="tracks" ref="schedule_grid">
                <template v-if="day === 'all'">
                    <ul class="stage-tracks">
                        <li v-for="(group, index) in schedule" :key="group.index" class="track" :class="{ alt: index % 2 !== 0 }" :style="`--index: ${group.index}`">
                            <box class="row-box" inert></box>
                            <h3 class="track-name" :style="`--index: ${group.index}`">
                                {{ group.name }}
                                <span class="desc" v-if="group.location?.description" v-html="group.location.description"></span>
                            </h3>
                            <template v-for="session in group.sessions" :key="`card-${session.id}`" class="session-item">
                                <SessionsCalendarCard
                                    :session="session"
                                    :class="{ 'session-short': session.grid_row_span === 1, 'is_even': group.index % 2 === 0 }"
                                    :day="day !== 'all'"
                                    :track_index="group.index"
                                    :style="`--index: ${group.index}; --spans: ${session.grid_row_span}; --starts: ${session.grid_row_start}`" />
                            </template>
                        </li>
                        <!-- Add empty columns if less than 4 -->
                        <li v-for="i in Math.max(0, 4 - schedule.length)" :key="`empty-${i}`" class="track" :style="`--index: ${schedule.length + i + 1}`">
                            <box class="row-box" inert></box>
                            <h3 class="track-name" :style="`--index: ${schedule.length + i + 1}`"></h3>
                        </li>
                    </ul>
                </template>
                <template v-else>
                    <template v-for="(group, location) in schedule" :key="location" class="track" :class="{ day: day !== 'all' }">
                        <box class="row-box" :class="{ alt: group.index % 2 !== 0 }" :style="`--index: ${group.index}`" inert></box>
                        <h3 class="track-name" :style="`--index: ${group.index}`">
                            {{ group.name }}
                            <span class="desc" v-if="group.location?.description" v-html="group.location.description"></span>
                        </h3>
                        <template v-for="session in group.sessions" :key="`card-${session.id}`" class="session-item">
                            <SessionsCalendarCard
                                :session="session"
                                :class="{ 'session-short': session.grid_row_span === 1 }"
                                :day="day !== 'all'"
                                :track_index="group.index"
                                :style="`--index: ${group.index}; --spans: ${session.grid_row_span}; --starts: ${session.grid_row_start}`" />
                        </template>
                    </template>
                    <!-- Add empty columns if less than 4 -->
                    <template v-for="i in Math.max(0, 4 - schedule.length)" :key="`empty-${i}`" class="track">
                        <box class="row-box" :style="`--index: ${schedule.length + i + 1}`" inert></box>
                        <h3 class="track-name" :style="`--index: ${schedule.length + i + 1}`"></h3>
                    </template>
                    <template v-for="i in time_blocks.length" :key="`line-${i}`" class="time-block-placeholder">
                        <line class="row-line" :class="{ hour: i === 1 || (i - 1) % 4 === 0 }" :style="`--starts: ${i + 1}`" inert tabindex="-1"></line>
                    </template>
                    <template v-for="block in empty_hour_blocks" :key="block.index" class="empty-block">
                        <div
                            class="session-recommend"
                            :style="`--index: ${block.grid_col_start}; --starts: ${block.grid_row_start}; --spans: ${block.grid_row_span}; --columns: ${Math.max(
                                schedule.length,
                                4
                            )}; --max-width: ${maxGridWidth}`">
                            <div class="message flex flex-col items-center justify-center sticky">
                                <h4>Find a Session</h4>
                                <p>Let's help fill this time slot</p>
                                <UButton variant="ghost" color="magenta" @click="handleSuggestClick(block.hourStart, block.hourEnd)"> Search Sessions </UButton>
                            </div>
                        </div>
                    </template>
                </template>
            </div>
        </div>
        <globalBackToTop v-if="day !== 'all'"></globalBackToTop>
    </div>
</template>

<style>
    .sessions-calendar {
        position: relative;
    }

    .sessions-calendar.loading {
        pointer-events: none;
        opacity: 0.5;
    }

    /* Back to top */
    .sessions-calendar .back-to-top {
        height: 7.3125rem;
    }

    @media (min-width: 1024px) {
        .sessions-calendar .back-to-top {
            margin-bottom: -5.5rem;
        }
    }

    .sessions-calendar .back-to-top button {
        left: 0;
    }

    .sessions-calendar .back-to-top.is-fixed button,
    .sessions-calendar .back-to-top.offscreen button {
        left: var(--box-padding-l);
    }

    /* Result count */
    .sessions-calendar .result-count {
        margin-right: auto;
        font-size: calc(15 / var(--base-fs) * 1rem);
        color: var(--teal-gray);
        font-weight: 300;
        line-height: 1.5625rem;
        letter-spacing: 0.01875;
        white-space: nowrap;
    }

    @media (min-width: 1024px) {
        .sessions-calendar .result-count {
            display: none;
        }
    }

    /* Session recommend */
    .sessions-calendar .session-recommend {
        position: sticky;
        left: 1rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius);
        margin: 0.0625rem 1rem;
        max-width: var(--max-width, 100%);
        background: var(--red-light);
        color: var(--magenta);
        grid-row: var(--starts) / span var(--spans);
        grid-column: var(--index) / span var(--columns);
        z-index: 0;
    }

    .sessions-calendar .session-recommend h4 {
        text-align: center;
        font-style: normal;
        font-weight: 500;
        font-size: 1.125rem;
        line-height: 1.25rem;
        letter-spacing: -0.03rem;
        margin-bottom: 0.4375rem;
    }

    @media (min-width: 1024px) {
        .sessions-calendar .session-recommend h4 {
            font-size: 1.5rem;
            line-height: 2rem;
            letter-spacing: -0.03rem;
        }
    }

    .sessions-calendar .session-recommend p {
        text-align: center;
        font-style: normal;
        font-weight: 300;
        font-size: 0.875rem;
        line-height: 1.3125rem;
        letter-spacing: 0.01875rem;
    }

    @media (min-width: 1024px) {
        .sessions-calendar .session-recommend p {
            font-size: 0.9375rem;
            line-height: 1.3125rem;
            letter-spacing: 0.01875rem;
        }
    }

    /* Schedule */
    .sessions-calendar .schedule {
        --row-height: 6.625rem;
        --column-width: 15rem;
        border: 1px solid var(--teal-light-line);
        border-radius: var(--radius);
    }

    /* Schedule - all mode */
    .sessions-calendar .schedule.all {
        display: flex;
        flex-direction: column;
        width: 100%;
        overflow-x: scroll;
        scroll-snap-type: x mandatory;
        -ms-overflow-style: none;
        scrollbar-width: none;
    }

    .sessions-calendar .schedule.all::-webkit-scrollbar {
        display: none;
    }

    .sessions-calendar .schedule.all .stage-tracks {
        display: flex;
        flex-direction: row;
    }

    .sessions-calendar .schedule.all .stage-tracks h3 {
        border-bottom: 1px solid var(--teal-gray);
    }

    .sessions-calendar .schedule.all .stage-tracks li {
        min-width: var(--column-width);
        border-right: 1px solid var(--teal-light-line);
        scroll-snap-align: start;
    }

    .sessions-calendar .schedule.all .stage-tracks li:last-child {
        border-right: none;
    }

    .sessions-calendar .schedule.all .stage-tracks li .session-cal-card {
        z-index: 1;
        margin: 0;
        width: 100%;
        border-radius: 0;
        border-bottom: 1px solid var(--teal-light-line);
    }

    .sessions-calendar .schedule.all .stage-tracks li .session-cal-card:last-child {
        border-bottom: none;
    }

    /* Schedule - day mode */
    .sessions-calendar .schedule.day {
        display: flex;
        width: 100%;
    }

    .sessions-calendar .schedule.day .timeline {
        display: grid;
        grid-template-rows: 5.1875rem repeat(var(--rows), var(--row-height));
        grid-template-columns: repeat(1, 7.625rem);
    }

    .sessions-calendar .schedule.day .timeline .time-label {
        grid-row: var(--starts) / span 4;
        font-size: 1rem;
        font-weight: 500;
        line-height: 1.125rem;
        letter-spacing: -0.0084375rem;
        border-top: 1px solid var(--teal-gray);
        padding: 0.8125rem 0 0 0.8125rem;
    }

    .sessions-calendar .schedule.day .tracks {
        display: grid;
        grid-template-rows: 5.1875rem repeat(var(--rows), var(--row-height));
        grid-template-columns: repeat(var(--columns), minmax(var(--column-width), 1fr));
        grid-column-gap: 0px;
        overflow-x: scroll;
        scroll-snap-type: x mandatory;
        -ms-overflow-style: none;
        scrollbar-width: none;
    }

    .sessions-calendar .schedule.day .tracks::-webkit-scrollbar {
        display: none;
    }

    .sessions-calendar .schedule.day .session-cal-card {
        z-index: 1;
        grid-row: var(--starts) / span var(--spans);
        grid-column: var(--index);
        margin: 0.0625rem 1rem;
    }

    .sessions-calendar .schedule.day .row-box {
        position: relative;
        z-index: 0;
        grid-row: 1 / span calc(var(--rows) + 1);
        grid-column: var(--index);
        background: transparent;
        scroll-snap-align: start;
    }

    .sessions-calendar .schedule.day .row-box.alt {
        background: var(--cream-alt);
    }

    .sessions-calendar .schedule.day .row-line {
        position: relative;
        z-index: 0;
        grid-row: var(--starts);
        grid-column: 1 / span var(--columns);
        height: 1px;
        background: var(--teal-light-line);
    }

    .sessions-calendar .schedule.day .row-line.hour {
        background: var(--teal-gray);
    }

    /* Schedule h3 */
    .sessions-calendar .schedule h3 {
        display: flex;
        align-items: center;
        flex-direction: column;
        justify-content: center;
        z-index: 1;
        text-wrap: balance;
        text-align: center;
        font-size: 1rem;
        font-weight: 500;
        line-height: 1.125rem;
        letter-spacing: -0.0075rem;
        padding: 1.96875rem 0.875rem 1.71875rem 0.875rem;
        height: 5.1875rem;
        margin: 0;
        grid-row: 1;
        grid-column: var(--index);
    }

    .sessions-calendar .schedule h3 span {
        padding-top: 0.375rem;
        display: block;
        color: var(--teal-light);
        text-align: center;
        font-size: 0.8125rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.125rem;
        letter-spacing: -0.0060625rem;
    }

    /* Track alternating background */
    .sessions-calendar .schedule .track:nth-child(even) {
        background: var(--cream-alt);
    }

    /* Sticky list */
    .sessions-calendar .sticky-list {
        z-index: 999999;
        position: fixed;
        top: 0;
        margin: 0 auto;
        display: flex;
        width: calc(90dvw);
        height: 5.1875rem;
        max-width: 1359px;
        background: var(--cream);
        overflow: hidden;
        border-bottom: 1px solid var(--teal);
        border-right: 1px solid var(--teal-light-line);
        transform: translateY(-100%);
        will-change: transform, opacity;
        transition: all 500ms ease-in-out;
        pointer-events: none;
    }

    @media (min-width: 1024px) {
        .sessions-calendar .sticky-list {
            width: calc((1360 / 1440 * 100vw) - 1px);
        }
    }

    .sessions-calendar .sticky-list .controls {
        position: absolute;
        left: 0.8125rem;
        right: auto;
        top: 0.95rem;
        background: var(--cream);
        border-radius: var(--radius);
    }

    .sessions-calendar .sticky-list .tracks {
        pointer-events: none;
    }

    .sessions-calendar .sticky-list .track {
        scroll-snap-align: start;
    }

    .sessions-calendar .sticky-list .track:nth-child(even) {
        background: var(--cream);
    }

    .sessions-calendar .sticky-list .track:nth-child(odd) {
        background: var(--cream-alt);
    }

    html.down .sessions-calendar .sticky-list.active {
        transform: translateY(0);
        pointer-events: all;
    }
</style>
